<template>
    <div class="chat-wrapper d-flex flex-column overflow-auto">
        <div class="d-flex p-2 align-items-center header">
            <CoRoundButton @click="back()" variant="light" icon="arrow-left" />
            <CoHeadline :level="3" truncate class="m-0 px-3">
                {{ thread['Title'] }}
            </CoHeadline>
            <CoButton
                v-if="amIinitiator"
                :label="$t('labels.save')"
                variant="primary"
                :disabled="!hasChanges"
                class="ml-auto"
                @click="publishUpdate()"
            />
            <CoConfirmation
                v-else
                :message="$t('messages.chat.leavechatconfirm')"
                confirm-variant="danger"
                @confirm="leaveChat"
                class="flex-shrink-0 ml-auto"
            >
                <CoButton variant="danger" icon="box-arrow-right" :label="$t('labels.leave')" />
            </CoConfirmation>
        </div>
        <div class="d-flex w-100 justify-content-center my-4">
            <!-- avatar -->
            <div>
                <CoThumbnail
                    v-if="(thread['ImageURL'] && thread['ImageURL'].length > 0) || !amIinitiator"
                    :imageURL="thread['ImageURL']"
                    :user="thread.Peer"
                    alt=""
                    :clickable="false"
                    :deletable="amIinitiator"
                    @delete.stop="removeImage"
                    content-type="user"
                    icon="people"
                    size="lg"
                    round
                    class="mb-1"
                />
                <CoDragDropFileUpload
                    v-else
                    class="mb-1"
                    :title="$t('placeholders.imagedragdropupload')"
                    :style="{
                        width: '9rem',
                        height: '9rem',
                        borderRadius: '50%',
                        overflow: 'hidden',
                        fontSize: '0.875rem',
                    }"
                    @handleInput="previewGroupImage"
                    :supportedFile="'image.*'"
                    :multiple="false"
                    subtitle=""
                />
            </div>
        </div>
        <!-- chat name section -->
        <CoCard variant="border" v-if="amIinitiator" class="mb-3 px-3 flex-shrink-0 flex-grow-0" body-class="p-0">
            <CoFormGroup :label="$t('labels.groupname')" class="m-0 mt-2 mb-3">
                <CoInput
                    type="text"
                    v-model="thread['Title']"
                    :placeholder="$t('placeholders.groupname')"
                    background="transparent"
                    required
                />
            </CoFormGroup>
        </CoCard>
        <!-- chat members section -->
        <CoCard variant="border" body-class="p-0" class="flex-shrink-0 pb-2 mb-3 flex-grow-0">
            <div class="d-flex justify-content-between align-items-baseline mt-2 px-3">
                <div class="d-flex align-items-center">
                    <CoIcon name="people" />
                    <CoText type="p2">({{ thread.Peers.length }}) {{ $tc('labels.member', 2) }}</CoText>
                </div>
                <CoAddMemberDialogue v-if="amIinitiator" :members="thread.Peers" @add-members="addMembers">
                    <CoRoundButton variant="icon" icon="person-plus" :title="$t('labels.addmember')" />
                </CoAddMemberDialogue>
            </div>
            <div class="mt-3 overflow-auto user-select px-3">
                <div class="d-flex flex-column">
                    <div v-if="thread.Peers != null && thread.Peers.length == 0" class="text-center">
                        {{ $t('messages.chat.nomembers') }}
                    </div>
                    <!-- always show my profile first -->
                    <div class="d-flex align-items-center justify-content-between py-2">
                        <CoUser
                            :user="{
                                ...me,
                                ...me.Profile,
                            }"
                        />
                        <CoBadge
                            v-if="thread.InitiatorID === me.ID"
                            variant="primary"
                            class="ml-2"
                            :label="$t('labels.owner')"
                        />
                    </div>
                    <div
                        v-for="(user, i) in thread.Peers"
                        v-if="user.ID !== $store.state.me.ID"
                        :key="user.ID"
                        class="d-flex align-items-center justify-content-between py-2"
                    >
                        <CoUser
                            :user="{
                                ...user,
                                ...user.Profile,
                            }"
                        />
                        <CoBadge
                            v-if="thread.InitiatorID === user.ID"
                            variant="primary"
                            class="ml-2"
                            :label="$t('labels.owner')"
                        />
                        <CoRoundButton
                            v-if="user.ID != $store.state.me.ID && amIinitiator"
                            @click="removeMember(i)"
                            variant="icon"
                            icon="trash"
                            class="ml-2"
                        />
                    </div>
                </div>
            </div>
        </CoCard>
    </div>
</template>

<style lang="less" scoped>
.chat-wrapper {
    height: calc(100vh - 159px);
    overflow: auto;
    @media (max-width: 768px) {
        max-height: calc(100vh - 144px);
        height: calc(100vh - 144px);
    }
}

.header {
    border: 1px solid var(--c-midgrey);
    border-radius: 1.125rem;
}
</style>

<script>
import Vue from 'vue';
import moment from 'moment';
import axios from 'axios';
import EventBus from '../../eventBus';
import CoUser from '@/components/Molecules/co-user/CoUser.vue';
import CoRoundButton from '@/components/Atoms/co-round-button/CoRoundButton.vue';
import CoThumbnail from '@/components/Molecules/co-thumbnail/CoThumbnail.vue';
import CoDragDropFileUpload from '@/components/Organisms/co-drag-drop-file-upload/CoDragDropFileUpload.vue';
import CoInput from '@/components/Molecules/co-input/CoInput.vue';
import CoAddMemberDialogue from '@/components/Organisms/co-add-member-dialogue/CoAddMemberDialogue.vue';
import CoConfirmation from '@/components/Molecules/co-confirmation/CoConfirmation.vue';
import CoHeadline from '@/components/Atoms/co-headline/CoHeadline.vue';
import CoButton from '@/components/Atoms/co-button/CoButton.vue';
import CoFormGroup from '@/components/Molecules/co-form-group/CoFormGroup.vue';
import CoBadge from '@/components/Atoms/co-badge/CoBadge.vue';
import CoCard from '@/components/Molecules/co-card/CoCard.vue';
import CoIcon from '@/components/Atoms/co-icon/CoIcon.vue';

export default {
    name: 'EditChat',
    components: {
        CoUser,
        CoRoundButton,
        CoThumbnail,
        CoDragDropFileUpload,
        CoInput,
        CoAddMemberDialogue,
        CoHeadline,
        CoButton,
        CoFormGroup,
        CoConfirmation,
        CoBadge,
        CoCard,
        CoIcon,
    },
    data() {
        return {
            threadID: this.$route.params.slug,
            thread: JSON.parse(JSON.stringify(this.$store.state.threads[this.$route.params.slug])),
            originalThread: JSON.parse(JSON.stringify(this.$store.state.threads[this.$route.params.slug])),
            isLoading: false,
            usersearch: '',
            users: [],
            searchingUsers: false,
            usersRerender: 0,
            community: [],
        };
    },
    watch: {
        $route(to, from) {
            this.threadID = to.params.slug;
            this.thread = JSON.parse(JSON.stringify(this.$store.state.threads[this.$route.params.slug]));
        },
    },
    computed: {
        amIinitiator() {
            return this.$store.state.me.ID === this.thread.InitiatorID;
        },
        hasChanges() {
            return JSON.stringify(this.thread) !== JSON.stringify(this.originalThread);
        },
        me() {
            return this.thread.Peers.find((item) => item.ID === this.$store.state.me.ID);
        },
    },
    methods: {
        previewGroupImage(event) {
            const { file } = event;
            this.thread.ImageURL = event.filePreview;
            this.thread.ImageFile = file;
            this.thread.ImageURLKey += 1;
        },
        back() {
            // warn about unsaved changes
            if (this.hasChanges) {
                const confirmLeave = confirm(this.$t('messages.unsavedchangeswarning'));
                if (!confirmLeave) {
                    return;
                }
            }
            this.$router.push(`/messenger/${this.threadID}`);
        },

        removeMember(index) {
            this.thread.Peers.splice(index, 1);
        },

        addMembers(users) {
            users.forEach((user) => {
                const Gotuser = this.thread.Peers.find((item) => item.ID === user.ID);
                if (Gotuser == null) {
                    this.thread.Peers.push(user);
                }
            });
        },

        removeImage() {
            this.thread.ImageURL = null;
            this.thread.ImageFile = null;
            this.thread.ImageURLKey += 1;
        },

        uploadImage() {
            this.$store
                .dispatch('imageUpload', {
                    file: this.thread.ImageFile,
                    url: '/upload/image/group-chat-avater',
                })
                .then((response) => {
                    this.thread.ImageURL = response;
                    this.updateThread();
                })
                .catch((error) => {
                    this.isLoading = false;
                    const errorMsg = {
                        Message: 'Could not upload image, please try again.',
                        Details: error.response.data,
                    };
                    if (
                        error.response &&
                        error.response.data &&
                        error.response.data.message === 'Request Entity Too Large'
                    ) {
                        errorMsg.Message = 'The file you tried to upload is too large. Max 10 MB.';
                    }
                    EventBus.$emit('ERROR', errorMsg);
                });
        },

        publishUpdate(event) {
            if (!this.thread.Title || this.thread.Title.length === 0) {
                const errorMsg = {
                    Message: this.$t('messages.chat.groupnameempty'),
                };
                EventBus.$emit('ERROR', errorMsg);
                return;
            }
            this.isLoading = true;

            if (this.thread.ImageFile != null) {
                this.uploadImage();
            } else {
                this.updateThread();
            }
        },

        updateThread() {
            const data = new Object(this.thread);
            delete data.ImageFile;
            const participants = [];
            this.thread.Peers.forEach((element) => {
                participants.push(element.ID);
            });
            data.Participants = participants;
            delete data.Peer;
            delete data.Peers;

            axios({
                method: 'PUT',
                url: '/chat/t/group-chat',
                withCredentials: true,
                data: JSON.stringify(data),
                headers: {
                    'Content-Type': 'application/json',
                },
            })
                .then((response) => {
                    if (response && response.data) {
                        // this.thread = response.data
                        // this.$store.commit("SET_THREAD", response.data, this.threadID);
                        EventBus.$emit('UPDATE_THREAD');
                        this.$router.push(`/messenger/${this.threadID}`);
                    }
                    this.isLoading = false;
                })
                .catch((error) => {
                    this.isLoading = false;
                    console.log(error);
                });
        },
        leaveChat() {
            this.isLoading = true;
            axios({
                method: 'POST',
                url: `/chat/t/leave/${this.threadID}`,
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json',
                },
            })
                .then((response) => {
                    EventBus.$emit('UPDATE_THREAD');
                    this.$router.push('/messenger');
                    this.isLoading = false;
                })
                .catch((error) => {
                    this.isLoading = false;
                    console.log(error);
                });
        },
    },
};
</script>
